import React from "react";

// Reusable skeleton primitives.
//
// `Skeleton` is the atomic shimmer block — a div with animate-pulse and
// a muted background. Everything else in this file composes it into
// page-shaped placeholders, so the layout doesn't jump when real data
// lands.
//
// Why CSS variables instead of hardcoded grays: the app supports five
// themes (Soft Violet / Royal Indigo / Fresh Emerald / Sunset Amber /
// Cool Slate) and the placeholder color should track surface-muted so
// it never reads as a foreign color.

export const Skeleton = ({ className = "", as: Tag = "div" }) => (
  <Tag
    className={`animate-pulse rounded bg-[var(--color-surface-muted)] ${className}`}
  />
);

// One profile card placeholder. Mirrors the compact card used on the
// bureau Dashboard, FindMatches, and PublicProfiles (image-less variant
// — heart/kebab corner buttons are omitted since they aren't part of
// the visible "shape" the eye anchors to).
export const ProfileCardSkeleton = () => (
  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
    <Skeleton className="h-5 w-2/3 mb-2" />
    <Skeleton className="h-3 w-1/3 mb-4" />
    <div className="space-y-2">
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
      <Skeleton className="h-3 w-4/6" />
    </div>
    <div className="mt-4 pt-3 border-t border-[var(--color-border)] flex justify-between">
      <Skeleton className="h-3 w-1/3" />
      <Skeleton className="h-3 w-12" />
    </div>
  </div>
);

// Client-card placeholder for the Matches Tracking index (one card per
// client, with status pills + summary footer).
export const ClientCardSkeleton = () => (
  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex-1 min-w-0">
        <Skeleton className="h-5 w-3/4 mb-2" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <div className="text-right">
        <Skeleton className="h-7 w-10 mb-2 ml-auto" />
        <Skeleton className="h-2 w-12 ml-auto" />
      </div>
    </div>
    <div className="flex flex-wrap gap-1.5 mb-3">
      <Skeleton className="h-5 w-14 rounded-full" />
      <Skeleton className="h-5 w-16 rounded-full" />
      <Skeleton className="h-5 w-12 rounded-full" />
    </div>
    <div className="pt-3 border-t border-[var(--color-border)] flex justify-between">
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className="h-3 w-16" />
    </div>
  </div>
);

// Single row placeholder for the referrals table. Caller decides how
// many to render so we don't second-guess pagination.
export const TableRowSkeleton = ({ columns = 4 }) => (
  <tr className="border-b border-[var(--color-border)]">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton className="h-3 w-3/4" />
        {i < 2 && <Skeleton className="h-2 w-1/2 mt-2" />}
      </td>
    ))}
  </tr>
);

// Grid wrappers — saves callers from repeating the same map().
export const ProfileGridSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <ProfileCardSkeleton key={i} />
    ))}
  </div>
);

export const ClientGridSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <ClientCardSkeleton key={i} />
    ))}
  </div>
);

// Centered branded spinner for very brief gates (auth check, route
// guard) where we don't yet know which page is about to render — so a
// content-shaped skeleton would be misleading.
export const FullScreenLoader = ({ label }) => (
  <div
    role="status"
    aria-live="polite"
    className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-[var(--color-background)] z-50"
  >
    <div
      className="h-10 w-10 rounded-full border-4 border-[var(--color-surface-muted)] border-t-[var(--color-primary)] animate-spin"
      aria-hidden="true"
    />
    {label && (
      <div className="text-sm text-[var(--color-text-muted)]">{label}</div>
    )}
  </div>
);

export default Skeleton;
